import React, { useState } from "react";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { getPaymentById, getUnrecordedPaidAmount, resetUnrecordedPayment } from "../services/paymentService.js";
import { PaymentCorrectionHost } from "./PaymentCorrection.jsx";

/**
 * Edit / void / reset actions for the payment history shown inside an invoice or purchase bill.
 * billType: 'invoice' | 'purchase'
 */
export function useBillPaymentCorrections({ billType, bill, onChanged }) {
  const { confirmDanger } = useDangerConfirm();
  const [editPayment, setEditPayment] = useState(null);
  const [voidingPayment, setVoidingPayment] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const unrecordedAmount = getUnrecordedPaidAmount(bill);

  const loadPayment = async (entry, open) => {
    if (!entry?.paymentId) return;
    setBusyId(entry.paymentId);
    try {
      const payment = await getPaymentById(entry.paymentId);
      if (!payment) {
        alert("This payment record could not be found.");
        return;
      }
      if (payment.status === "VOID") {
        alert(`${payment.paymentNo} is already void.`);
        return;
      }
      open(payment);
    } catch (err) {
      alert(err.message || "Failed to load payment.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReset = async () => {
    const label = billType === "purchase" ? "paid to the vendor" : "received";
    const msg = `₹${unrecordedAmount.toLocaleString("en-IN")} is marked as ${label} on this bill without a payment record.\n\nReset it? The bill will show this amount as due again.`;
    if (!(await confirmDanger({ title: "Reset unrecorded payment?", message: msg, confirmLabel: "Reset" }))) return;
    setResetting(true);
    try {
      await resetUnrecordedPayment(billType, bill.id);
      if (onChanged) await onChanged();
    } catch (err) {
      alert(err.message || "Failed to reset payment.");
    } finally {
      setResetting(false);
    }
  };

  const handleChanged = async () => {
    if (onChanged) await onChanged();
  };

  const renderRowActions = (entry) => {
    if (entry.voided || !entry.paymentId) return null;
    const busy = busyId === entry.paymentId;
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => loadPayment(entry, setEditPayment)}
          style={{ fontSize: "0.7rem", padding: "2px 7px" }}
        >
          ✏️
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => loadPayment(entry, setVoidingPayment)}
          style={{ fontSize: "0.7rem", padding: "2px 7px", color: "var(--danger)" }}
          aria-label="Void payment"
        >
          🚫
        </Button>
      </>
    );
  };

  const unrecordedRow = unrecordedAmount > 0 ? (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.55rem 0.75rem",
        background: "rgba(245, 158, 11, 0.08)",
        borderTop: "1px solid var(--border-subtle)"
      }}
    >
      <div style={{ fontSize: "0.78rem", minWidth: 0 }}>
        <div style={{ fontWeight: "600", color: "var(--warning)" }}>
          Unrecorded payment ₹{unrecordedAmount.toLocaleString("en-IN")}
        </div>
        <div style={{ color: "var(--text-dim)", marginTop: "1px" }}>
          Marked paid without a receipt, so it can't be edited. Reset it if it was wrong.
        </div>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={resetting}
        onClick={handleReset}
        style={{ fontSize: "0.7rem", padding: "2px 8px", flexShrink: 0 }}
      >
        Reset
      </Button>
    </div>
  ) : null;

  const host = (
    <PaymentCorrectionHost
      editPayment={editPayment}
      voidingPayment={voidingPayment}
      onCloseEdit={() => setEditPayment(null)}
      onCloseVoid={() => setVoidingPayment(null)}
      onChanged={handleChanged}
    />
  );

  return { renderRowActions, unrecordedRow, unrecordedAmount, host };
}

export default useBillPaymentCorrections;
